import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { CADCurrencyConverter } from "./currency";
import { calculateBaseQuantity, itemUOMSelect, ItemUOMSelectResult } from "./item-uoms";
import { Item, itemSelect, ItemSelectResult, processItem } from "./items";

export interface Shipment extends Omit<ShipmentSelectResult, "lines"> {
  allLines: ShipmentLineItem[];
  jobLines: ShipmentLineItem[];
}

export interface ShipmentLineItem extends Omit<ShipmentLineItemSelectResult, "item"> {
  /** Quantity for the line item converted to the base UOM */
  baseQuantity: number;
  item: Item;
}

interface ShipmentSelectResult extends Pick<
  Tables<"Shipment">,
  "id" | "number" | "date" | "trackingNumber" | "trackingLink" | "comment"
> {
  shippingMethod?: Pick<Tables<"ShipMethod">, "id" | "name">;
  lines: ShipmentLineItemSelectResult[];
}

export interface ShipmentLineItemSelectResult extends Pick<
  Tables<"ShipmentLineItem">,
  "id" | "lineNumber" | "description"
> {
  item: ItemSelectResult;
  job: Pick<Tables<"Job">, "id" | "name"> | null;
  /** Quantity for the line item in the selected ItemUOM */
  uomQuantity: number;
  itemUOM: ItemUOMSelectResult | null;
  bin: Pick<Tables<"Bin">, "id" | "number"> | null;
}

const shipmentSelect = `
id,
number,
date,
shippingMethod:ShipMethod(id, name),
trackingNumber,
trackingLink,
comment,
lines:ShipmentLineItem!inner(
  id,
  lineNumber,
  item:Item(${itemSelect}),
  job:Job(id, name),
  description,
  uomQuantity:quantity,
  itemUOM:ItemUOM(${itemUOMSelect}),
  bin:Bin(id, number)
)
`;

export async function getShipments(
  jobId: number,
  binId: number,
  CADCurrencyConverter: CADCurrencyConverter,
): Promise<Shipment[]> {
  // Get all shipment ids with any non-deleted line item either assigned to the job, or the sales order rack bin
  const shipmentIds = new Set(
    (
      await supabase
        .from("ShipmentLineItem")
        .select("shipmentId")
        .eq("deleted", false)
        .or(`binId.eq.${binId.toFixed()},jobId.eq.${jobId.toFixed()}`)
        .throwOnError()
    ).data.map((x) => x.shipmentId),
  );

  // Get all non-deleted shipments with the matched shipment ids
  const queryResult = (
    await supabase
      .from("Shipment")
      .select<typeof shipmentSelect, ShipmentSelectResult>(shipmentSelect)
      .in("id", [...shipmentIds])
      .eq("deleted", false)
      .eq("lines.deleted", false)
      .order("date", { ascending: false })
      .order("lineNumber", { referencedTable: "lines", ascending: true })
      .throwOnError()
  ).data;

  // Do some additional client-side processing on the results before returning
  return queryResult.map((po) => processShipment(po, jobId, CADCurrencyConverter));
}

function processShipment(
  { lines: rawLines, ...shipment }: ShipmentSelectResult,
  jobId: number,
  CADCurrencyConverter: CADCurrencyConverter,
): Shipment {
  const allLines = rawLines.map((line) => processShipmentLineItem(line, CADCurrencyConverter));
  const jobLines = allLines.filter((x) => x.job?.id === jobId);
  return { ...shipment, allLines, jobLines };
}

function processShipmentLineItem(
  line: ShipmentLineItemSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): ShipmentLineItem {
  return {
    ...line,
    baseQuantity: calculateBaseQuantity(line.uomQuantity, line.itemUOM),
    item: processItem(line.item, CADCurrencyConverter),
  };
}
